/**
 * PATH: apps/quote/src/pages/builder/builder-workspace/WorkspaceHeader.tsx
 * PURPOSE: Compact header bar with quote info, totals, and action buttons
 */

import { useState } from 'react'
import { ArrowLeft, Calculator, ChevronDown, Copy, Paperclip, RefreshCw, Send } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { QuoteDetail } from '@/lib/quoteApi'
import { statusBadgeClass, getMarginColor } from './builder-helpers'

export interface WorkspaceHeaderProps {
  quote: QuoteDetail
  totals: { totalEx: number; marginPercent: number; manDays: number }
  onBack: () => void
  onRefresh: () => void
  onExportPdf: () => void
  onExportExcel: () => void
  onRecalculate: () => void
  onSubmit: () => void
  onAttach: () => void
  /** Duplicate the current quote. asRevision=true keeps the same quote_number
   * and bumps the revision (a new version); false makes a fresh copy. */
  onDuplicate: (asRevision: boolean) => void
  isRefreshing: boolean
  isPdfExporting: boolean
  isExcelExporting: boolean
  isRecalculating: boolean
  isSubmitting: boolean
  isDuplicating: boolean
}

export default function WorkspaceHeader({ quote, totals, onBack, onRefresh, onExportPdf, onExportExcel, onRecalculate, onSubmit, onAttach, onDuplicate, isRefreshing, isPdfExporting, isExcelExporting, isRecalculating, isSubmitting, isDuplicating }: WorkspaceHeaderProps) {
  const isEditable = !!quote.is_editable
  const [dupOpen, setDupOpen] = useState(false)

  return (
    <div className="bg-white dark:bg-slate-900 border-b">
      <div className="flex items-center justify-between px-3 py-2">
        {/* Left: Back + Quote Info */}
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800" title="Back to quotes">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 dark:text-white">{quote.quote_number}</span>
            <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide', statusBadgeClass(String(quote.status)))}>
              {quote.status_display || String(quote.status)}
            </span>
          </div>
          <span className="text-sm text-slate-500 dark:text-slate-400 hidden md:inline">
            {quote.client_name || 'No client'} • {quote.project_name || quote.project_address || 'No project'}
          </span>
        </div>

        {/* Center: Totals */}
        <div className="hidden lg:flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Total:</span>
            <span className="font-bold text-slate-900 dark:text-white">{formatCurrency(totals.totalEx)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Margin:</span>
            <span className={cn('font-semibold px-1.5 py-0.5 rounded text-xs', getMarginColor(totals.marginPercent))}>
              {totals.marginPercent ? `${totals.marginPercent.toFixed(1)}%` : '—'}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500">Days:</span>
            <span className="font-medium text-slate-700 dark:text-slate-300">{totals.manDays ? totals.manDays.toFixed(1) : '-'}</span>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          <button onClick={onRefresh} className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800" title="Refresh">
            <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
          </button>
          <button onClick={onAttach} className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800" title="Attach files">
            <Paperclip className="w-4 h-4" />
          </button>
          <div className="relative">
            <button
              onClick={() => setDupOpen((o) => !o)}
              disabled={isDuplicating}
              className="px-2.5 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1"
              title="Duplicate this quote or start a new revision"
            >
              <Copy className={cn('w-3.5 h-3.5', isDuplicating && 'animate-pulse')} />
              Duplicate
              <ChevronDown className="w-3 h-3" />
            </button>
            {dupOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setDupOpen(false)} />
                <div className="absolute right-0 mt-1 w-44 z-20 rounded-md border bg-white dark:bg-slate-800 dark:border-slate-700 shadow-lg py-1 text-sm">
                  <button
                    onClick={() => { setDupOpen(false); onDuplicate(false) }}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    Duplicate quote
                    <span className="block text-[11px] text-slate-400">Fresh copy, new number</span>
                  </button>
                  <button
                    onClick={() => { setDupOpen(false); onDuplicate(true) }}
                    className="w-full text-left px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700"
                  >
                    New revision
                    <span className="block text-[11px] text-slate-400">Same number, version +1</span>
                  </button>
                </div>
              </>
            )}
          </div>
          <button onClick={onExportPdf} disabled={isPdfExporting} className="px-2.5 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">PDF</button>
          <button onClick={onExportExcel} disabled={isExcelExporting} className="px-2.5 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">Excel</button>
          <button onClick={onRecalculate} disabled={isRecalculating} className="px-2.5 py-1.5 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1">
            <Calculator className={cn('w-3.5 h-3.5', isRecalculating && 'animate-spin')} />Recalc
          </button>
          {String(quote.status) === 'draft' && (
            <button onClick={onSubmit} disabled={!isEditable || isSubmitting} className="ml-1 px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
              <Send className="w-3.5 h-3.5" />Submit
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
